import { Switch, UnstyledButton, useMantineColorScheme } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import classes from "./ThemeSwitcher.module.css";

import { SettingRow, SettingRowSelect } from "@/components/elements/Settings";
import { IconMoon, IconSun, IconThemeDefault } from "@/components/icons";

import type { FC } from "react";

const THEME_BLUE = "#143AB1";
const THEME_BLACK = "#383838";
const THEME_PREFERENCE_KEY = "theme-preference";

type ThemePreference = "light" | "dark" | "system";

interface Props {
  layout?: "simple" | "complex";
}

type ThemeOption = {
  value: ThemePreference;
  Icon: typeof IconSun;
  labelKey: "header.theme.light" | "header.theme.dark" | "header.theme.system";
};

const themeOptions: ThemeOption[] = [
  { value: "light", Icon: IconSun, labelKey: "header.theme.light" },
  { value: "dark", Icon: IconMoon, labelKey: "header.theme.dark" },
  { value: "system", Icon: IconThemeDefault, labelKey: "header.theme.system" },
];

const getInitialPreference = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(THEME_PREFERENCE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }

    const mantineScheme = localStorage.getItem("mantine-color-scheme-value");
    if (mantineScheme === "auto") {
      return "system";
    }
    if (mantineScheme === "dark") {
      return "dark";
    }
  } catch {
    // localStorage may be unavailable
  }

  return "light";
};

const resolveColorScheme = (preference: ThemePreference): "light" | "dark" => {
  if (preference === "system") {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }

    return "light";
  }

  return preference;
};

const ThemeSwitcher: FC<Props> = ({ layout = "simple" }) => {
  const { t } = useTranslation();

  const { colorScheme, setColorScheme } = useMantineColorScheme({ keepTransitions: true });
  const [preference, setPreference] = useLocalStorage<ThemePreference>({
    key: THEME_PREFERENCE_KEY,
    defaultValue: getInitialPreference(),
  });

  const isDark = colorScheme === "dark";

  useEffect(() => {
    if (layout !== "complex") {
      return;
    }

    setColorScheme(resolveColorScheme(preference));
  }, [layout, preference, setColorScheme]);

  const handleToggle = () => {
    const next = isDark ? "light" : "dark";
    setColorScheme(next);
    setPreference(next);
  };

  const handleSelect = (value: ThemePreference) => {
    setPreference(value);
    setColorScheme(resolveColorScheme(value));
  };

  if (layout === "simple") {
    return (
      <SettingRow
        label={t("header.theme.title")}
        leftSection={t("header.theme.light")}
        rightSection={t("header.theme.dark")}
      >
        <Switch onChange={handleToggle} checked={colorScheme === "dark"} />
      </SettingRow>
    );
  }

  return (
    <SettingRowSelect label={t("header.theme.title")}>
      <div className={`${classes.pill} ${classes.complex}`}>
        {themeOptions.map(({ value, Icon, labelKey }) => {
          const isActive = preference === value;

          return (
            <UnstyledButton
              key={value}
              aria-label={t(labelKey)}
              aria-pressed={isActive}
              className={`${classes.option} ${isActive ? classes.optionActive : ""}`}
              onClick={() => handleSelect(value)}
            >
              <Icon
                size={22}
                color={
                  isActive ? (isDark ? "#7289D0" : THEME_BLUE) : isDark ? "#ffffff" : THEME_BLACK
                }
              />
            </UnstyledButton>
          );
        })}
      </div>
    </SettingRowSelect>
  );
};

export default ThemeSwitcher;
